import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import moment from "moment";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import AddBannedWordDialog from "./AddBannedWordDialog";
import BannedDictionaryPanel from "./BannedDictionaryPanel";
import BannedService from "./BannedService";
import BannedWordMeaningDialog from "./BannedWordMeaningDialog";
import Logger from "../../../services/Logger";

export default function BannedMain() {
    const { language } = useAdminLanguage();
    const [bannedWords, setBannedWords] = useState([]);
    const [reload, setReload] = useState();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");

    // Add word dialog state
    const [openAddDialog, setOpenAddDialog] = useState(false);

    // Confirmation dialog state
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);

    // Word meaning dialog state
    const [openMeaningDialog, setOpenMeaningDialog] = useState(false);
    const [selectedWordForMeaning, setSelectedWordForMeaning] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [pageTab, setPageTab] = useState(0);

    const categories = [
        "profanity",
        "violence",
        "drugs",
        "offensive",
        "inappropriate",
        "high_frequency",
        "other"
    ];

    useEffect(() => {
        BannedService.requestList(language).then((r) => {
            if (r) {
                setBannedWords(r);
            }
        });
    }, [reload, language]);

    const handleAddWord = async (word, category, language) => {
        try {
            await BannedService.addWord(category, word, "", language);
            setOpenAddDialog(false);
            handleSnackBar("success", "Word added successfully");
            setReload(moment());
            return true;
        } catch (error) {
            handleSnackBar("error", error?.message ?? "Failed to add word");
            Logger.error("Error adding word:", error);
            return false;
        }
    };

    const handleDeleteWord = async (id) => {
        try {
            await BannedService.deleteWord(id);
            handleSnackBar("success", "Word deleted successfully");
            setReload(moment());
        } catch (error) {
            handleSnackBar("error", "Failed to delete word");
            Logger.error("Error deleting word:", error);
        }
    };

    const handleDeleteClick = (word, event) => {
        event.stopPropagation();
        setWordToDelete(word);
        setOpenConfirmDialog(true);
    };

    const handleWordClick = (word) => {
        setSelectedWordForMeaning(word);
        setOpenMeaningDialog(true);
    };

    const handleConfirmDelete = (id) => {
        handleDeleteWord(id);
        setOpenConfirmDialog(false);
        setWordToDelete(null);
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    // Format category name for display (e.g., "high_frequency" -> "High Frequency")
    const formatCategoryLabel = (category) => {
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const groupWordsByCategory = () => {
        const grouped = {};
        bannedWords.forEach(word => {
            if (!grouped[word.category]) {
                grouped[word.category] = [];
            }
            grouped[word.category].push(word);
        });
        return grouped;
    };

    const groupedWords = groupWordsByCategory();
    const selectedCategory = categories[selectedTab] || categories[0];

    const handleTabChange = (_event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2, border: "1px solid #e0e0e0" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={pageTab}
                    onChange={(_event, value) => setPageTab(value)}
                    aria-label="Banned page"
                >
                    <Tab label="Banned words" />
                    <Tab label="Dictionary" />
                </Tabs>
            </Box>

            {pageTab === 1 ? (
                <BannedDictionaryPanel
                    language={language}
                    bannedWords={bannedWords}
                    onAdd={handleAddWord}
                />
            ) : (
            <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Banned Words Manager
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenAddDialog(true)}
                >
                    Add Word
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Banned word categories"
                >
                    {categories.map((category, index) => (
                        <Tab
                            key={category}
                            label={`${formatCategoryLabel(category)} (${groupedWords[category]?.length || 0})`}
                            value={index}
                        />
                    ))}
                </Tabs>
            </Box>

            <Box>
                {selectedCategory === "high_frequency" && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        These words are allowed in <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>EASY</Box> puzzles.
                    </Typography>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {groupedWords[selectedCategory]?.map((word) => (
                        <Chip
                            key={word.id}
                            label={word.word}
                            onClick={() => handleWordClick(word)}
                            onDelete={(e) => handleDeleteClick(word, e)}
                            color="error"
                            variant="outlined"
                            size="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    ))}
                    {(!groupedWords[selectedCategory] || groupedWords[selectedCategory].length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                            No words in this category
                        </Typography>
                )}
            </Box>
            </Box>
            </>
            )}

            <AddBannedWordDialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                onAdd={handleAddWord}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Delete Banned Word"
                message="Are you sure you want to delete this banned word?"
                targetName={wordToDelete?.word}
                targetId={wordToDelete?.id}
                callback={handleConfirmDelete}
            />

            <BannedWordMeaningDialog
                open={openMeaningDialog}
                onClose={() => setOpenMeaningDialog(false)}
                word={selectedWordForMeaning}
            />
        </Box>
    );
}
